import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FALLBACK_LINKS = [
  { key: 'home',   to: '/',      label: 'Home' },
  { key: 'search', to: '/search',label: 'Search' },
  { key: 'blog',   to: '/blog',  label: 'Blog' },
  { key: 'about',  to: '/about', label: 'About' },
];

export default function Navbar({ cartCount, onCartClick, user }) {
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [links, setLinks]               = useState(FALLBACK_LINKS);
  const [searchOpen, setSearchOpen]     = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [suggestions, setSuggestions]   = useState([]);
  const [recommended, setRecommended]   = useState([]);
  const [recLoaded, setRecLoaded]       = useState(false);
  const [sugLoading, setSugLoading]     = useState(false);
  const [activeSug, setActiveSug]       = useState(-1);

  const searchInputRef = useRef(null);
  const suggestTimer   = useRef(null);
  const location       = useLocation();
  const navigate       = useNavigate();

  useEffect(() => { setMobileOpen(false); setSearchOpen(false); }, [location]);

  /* ── Price formatter ── */
  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : null;

  /* ── Open ── */
  const openSearch = () => {
    setSearchOpen(true);
    setSearchQuery('');
    setSuggestions([]);
    setActiveSug(-1);
    setTimeout(() => searchInputRef.current?.focus(), 50);
    // Fetch top-sellers once per session
    if (!recLoaded) {
      fetch(`${API_BASE}/api/products?sort=popular`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => {
          const list = (data.products || []).slice(0, 6).map(p => {
            const pricingList = (p.pricing || []).filter(e => e.price != null);
            let price = p.price ?? null;
            let originalPrice = p.originalPrice ?? null;
            if (pricingList.length > 0) {
              const min = pricingList.reduce((m, e) => e.price < m.price ? e : m, pricingList[0]);
              price = min.price;
              originalPrice = min.originalPrice ?? null;
            }
            return {
              _id: p._id,
              name: p.name,
              slug: p.slug,
              image: p.image || p.images?.[0]?.url || '',
              price,
              originalPrice,
              badge: p.badge,
              reviews: p.reviews,
              rating: p.rating,
            };
          });
          setRecommended(list);
          setRecLoaded(true);
        })
        .catch(() => {});
    }
  };

  /* ── Close ── */
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSuggestions([]);
    setActiveSug(-1);
    clearTimeout(suggestTimer.current);
  }, []);

  /* ── Esc key ── */
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') closeSearch(); };
    if (searchOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen, closeSearch]);

  /* ── Debounced suggestion fetch ── */
  useEffect(() => {
    clearTimeout(suggestTimer.current);
    const term = searchQuery.trim();
    if (term.length < 2) { setSuggestions([]); setSugLoading(false); return; }

    setSugLoading(true);
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/products/suggest?q=${encodeURIComponent(term)}&limit=6`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSugLoading(false);
      }
      setActiveSug(-1);
    }, 280);

    return () => clearTimeout(suggestTimer.current);
  }, [searchQuery]);

  /* ── Submit ── */
  const submitSearch = (e) => {
    e?.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    closeSearch();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  /* ── Go to product ── */
  const goTo = (slug) => { closeSearch(); navigate(`/product/${slug}`); };

  /* ── Keyboard nav ── */
  const handleKeyDown = (e) => {
    const list = searchQuery.trim().length >= 2 ? suggestions : recommended;
    if (!list.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveSug(i => Math.min(i + 1, list.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveSug(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && activeSug >= 0) { e.preventDefault(); goTo(list[activeSug].slug); }
  };

  /* ── Nav items ── */
  const buildTo = (href, query) => (query ? `${href}?${query}` : href);

  const fetchNavItems = () => {
    fetch(`${API_BASE}/api/nav-items`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(items => {
        if (Array.isArray(items) && items.length > 0) {
          setLinks(items.map(item => ({
            key: item._id || item.href,
            to: buildTo(item.href, item.query),
            label: item.label,
            external: item.external || false,
            hasMegaMenu: !!item.hasMegaMenu && Array.isArray(item.subItems) && item.subItems.length > 0,
            description: item.description || '',
            image: item.image?.url || '',
            subItems: (item.subItems || []).map(s => ({
              key: s._id || s.href,
              to: buildTo(s.href, s.query),
              label: s.label,
              external: s.external || false,
              description: s.description || '',
              badge: s.badge || '',
              image: s.image?.url || '',
            })),
          })));
        }
      })
      .catch(() => {});
  };

  useEffect(() => { fetchNavItems(); }, []);

  const [megaOpenIdx, setMegaOpenIdx]         = useState(null);
  const [mobileExpandedIdx, setMobileExpandedIdx] = useState(null);
  const megaCloseTimer = useRef(null);

  const openMega  = (idx) => { clearTimeout(megaCloseTimer.current); setMegaOpenIdx(idx); };
  const closeMega = () => { megaCloseTimer.current = setTimeout(() => setMegaOpenIdx(null), 120); };

  useEffect(() => { setMegaOpenIdx(null); setMobileExpandedIdx(null); }, [location]);

  // Below this width the hamburger/accordion nav is shown (matches the
  // 768px CSS breakpoint), so a tap on a parent item should open its
  // sub-menu instead of navigating straight to the parent link.
  const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= 768;

  const handleParentTap = (e, idx) => {
    if (isMobileViewport()) {
      e.preventDefault();
      setMobileExpandedIdx(i => i === idx ? null : idx);
    }
  };

  const handleHamburgerClick = () => {
    setMobileOpen(o => { if (!o) fetchNavItems(); return !o; });
  };

  /* ── Shared product card (used in both recommended + results) ── */
  const ProductCard = ({ p, index }) => {
    const discount = p.originalPrice && p.originalPrice > p.price
      ? Math.round((1 - p.price / p.originalPrice) * 100)
      : null;
    return (
      <div
        className={`nsearch__card${activeSug === index ? ' nsearch__card--active' : ''}`}
        onClick={() => goTo(p.slug)}
        onMouseEnter={() => setActiveSug(index)}
        onMouseLeave={() => setActiveSug(-1)}
      >
        <div className="nsearch__card-img-wrap">
          {p.image
            ? <img src={p.image} alt={p.name} className="nsearch__card-img" />
            : <span className="nsearch__card-placeholder">🌿</span>
          }
          {discount && <span className="nsearch__card-badge">−{discount}%</span>}
          {!discount && p.badge && <span className="nsearch__card-badge nsearch__card-badge--label">{p.badge}</span>}
        </div>
        <div className="nsearch__card-body">
          <p className="nsearch__card-name">{p.name}</p>
          {p.rating > 0 && (
            <div className="nsearch__card-rating">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              <span>{Number(p.rating).toFixed(1)}</span>
              {p.reviews > 0 && <span className="nsearch__card-reviews">({p.reviews})</span>}
            </div>
          )}
          <div className="nsearch__card-prices">
            {fmt(p.price) && <span className="nsearch__card-price">{fmt(p.price)}</span>}
            {fmt(p.originalPrice) && p.originalPrice > p.price && (
              <span className="nsearch__card-original">{fmt(p.originalPrice)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const isSearching = searchQuery.trim().length >= 2;

  return (
    <header className="navbar">
      <nav className="navbar__inner">
        <button className="navbar__hamburger" onClick={handleHamburgerClick} aria-label="Menu">
          <span /><span /><span />
        </button>

        <Link to="/" className="navbar__logo">
          <img src="/Naturiq_logo.png" alt="Naturiq" className="navbar__logo-img" />
        </Link>

        <ul className={`navbar__links${mobileOpen ? ' navbar__links--open' : ''}`}>
          {links.map((l, idx) => (
            <li
              key={l.key || idx}
              className={`navbar__item${l.hasMegaMenu ? ' navbar__item--has-mega' : ''}`}
              onMouseEnter={() => l.hasMegaMenu && openMega(idx)}
              onMouseLeave={() => l.hasMegaMenu && closeMega()}
            >
              <div className="navbar__link-row">
                {l.external ? (
                  <a
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="navbar__link"
                    onClick={e => l.hasMegaMenu && handleParentTap(e, idx)}
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    to={l.to}
                    className={`navbar__link${location.pathname === l.to.split('?')[0] ? ' navbar__link--active' : ''}`}
                    onClick={e => l.hasMegaMenu && handleParentTap(e, idx)}
                  >
                    <span className="navbar__link-text">{l.label}</span>
                  </Link>
                )}
                {l.hasMegaMenu && (
                  <button
                    type="button"
                    className={`navbar__chevron${megaOpenIdx === idx || mobileExpandedIdx === idx ? ' navbar__chevron--open' : ''}`}
                    aria-label={`Show ${l.label} menu`}
                    onClick={() => setMobileExpandedIdx(i => i === idx ? null : idx)}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                )}
              </div>

              {/* ── Desktop: full-width creative mega-menu ── */}
              {l.hasMegaMenu && megaOpenIdx === idx && (
                <div className="nmega" onMouseEnter={() => openMega(idx)} onMouseLeave={closeMega}>
                  <div className="nmega__inner">
                    {(l.image || l.description) && (
                      <Link to={l.to} className="nmega__feature" onClick={() => setMegaOpenIdx(null)}>
                        {l.image ? (
                          <img src={l.image} alt={l.label} className="nmega__feature-img" />
                        ) : (
                          <div className="nmega__feature-img nmega__feature-img--blank" />
                        )}
                        <div className="nmega__feature-overlay">
                          <span className="nmega__feature-eyebrow">{l.label}</span>
                          {l.description && <p className="nmega__feature-desc">{l.description}</p>}
                          <span className="nmega__feature-cta">
                            Shop all
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                          </span>
                        </div>
                      </Link>
                    )}
                    <div className="nmega__grid">
                      {l.subItems.map((s, sIdx) => (
                        s.external ? (
                          <a key={s.key || sIdx} href={s.to} target="_blank" rel="noopener noreferrer" className="nmega__tile">
                            <span className="nmega__tile-img-wrap">
                              {s.image
                                ? <img src={s.image} alt={s.label} className="nmega__tile-img" />
                                : <span className="nmega__tile-placeholder">🌿</span>}
                              {s.badge && <span className="nmega__tile-badge">{s.badge}</span>}
                            </span>
                            <span className="nmega__tile-label">{s.label}</span>
                            {s.description && <span className="nmega__tile-desc">{s.description}</span>}
                          </a>
                        ) : (
                          <Link key={s.key || sIdx} to={s.to} className="nmega__tile" onClick={() => setMegaOpenIdx(null)}>
                            <span className="nmega__tile-img-wrap">
                              {s.image
                                ? <img src={s.image} alt={s.label} className="nmega__tile-img" />
                                : <span className="nmega__tile-placeholder">🌿</span>}
                              {s.badge && <span className="nmega__tile-badge">{s.badge}</span>}
                            </span>
                            <span className="nmega__tile-label">{s.label}</span>
                            {s.description && <span className="nmega__tile-desc">{s.description}</span>}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Mobile: inline expandable image list ── */}
              {l.hasMegaMenu && mobileExpandedIdx === idx && (
                <ul className="navbar__mobile-sub">
                  <li>
                    {l.external ? (
                      <a href={l.to} target="_blank" rel="noopener noreferrer" className="navbar__mobile-sub-viewall">
                        View all {l.label}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </a>
                    ) : (
                      <Link to={l.to} className="navbar__mobile-sub-viewall">
                        View all {l.label}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </Link>
                    )}
                  </li>
                  {l.subItems.map((s, sIdx) => (
                    <li key={s.key || sIdx}>
                      {s.external ? (
                        <a href={s.to} target="_blank" rel="noopener noreferrer" className="navbar__mobile-sub-link">
                          <span className="navbar__mobile-sub-thumb">
                            {s.image ? <img src={s.image} alt="" /> : <span>🌿</span>}
                          </span>
                          <span>
                            <span className="navbar__mobile-sub-label">{s.label}{s.badge && <em>{s.badge}</em>}</span>
                            {s.description && <span className="navbar__mobile-sub-desc">{s.description}</span>}
                          </span>
                        </a>
                      ) : (
                        <Link to={s.to} className="navbar__mobile-sub-link">
                          <span className="navbar__mobile-sub-thumb">
                            {s.image ? <img src={s.image} alt="" /> : <span>🌿</span>}
                          </span>
                          <span>
                            <span className="navbar__mobile-sub-label">{s.label}{s.badge && <em>{s.badge}</em>}</span>
                            {s.description && <span className="navbar__mobile-sub-desc">{s.description}</span>}
                          </span>
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        <div className="navbar__actions">
          <button className="navbar__icon-btn" aria-label="Search" onClick={openSearch}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
          <Link to={user ? '/account/orders' : '/account'} className="navbar__icon-btn" aria-label="Account" title={user ? `Hi, ${user.firstName || 'there'}` : 'Log in'}>
            {user ? (
              <span className="navbar__avatar">
                {`${user.firstName || ''} ${user.lastName || ''}`.trim().split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'}
              </span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </Link>
          <button className="navbar__cart-btn" onClick={onCartClick} aria-label="Cart">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            {cartCount > 0 && <span className="navbar__cart-badge">{cartCount}</span>}
          </button>
        </div>
      </nav>

      {/* ── Search panel (full-width, below navbar) ── */}
      {searchOpen && (
        <>
          {/* dim backdrop */}
          <div className="nsearch__backdrop" onClick={closeSearch} />

          {/* panel */}
          <div className="nsearch__panel">
            {/* Input bar */}
            <div className="nsearch__input-bar">
              <div className="nsearch__input-inner">
                {sugLoading ? (
                  <svg className="nsearch__spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                )}
                <form onSubmit={submitSearch} style={{ flex: 1, display: 'flex' }}>
                  <input
                    ref={searchInputRef}
                    className="nsearch__input"
                    type="text"
                    placeholder="Search plants, pots, and more…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                  />
                </form>
                {searchQuery && (
                  <button
                    type="button"
                    className="nsearch__clear"
                    onClick={() => { setSearchQuery(''); setSuggestions([]); setActiveSug(-1); searchInputRef.current?.focus(); }}
                    aria-label="Clear"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
                <button type="button" className="nsearch__close" onClick={closeSearch} aria-label="Close search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  <span>Close</span>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="nsearch__body">

              {/* ── Before typing: Top Selling recommended ── */}
              {!isSearching && (
                <div className="nsearch__section">
                  <div className="nsearch__section-header">
                    <span className="nsearch__section-title">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle', marginRight:6, marginBottom:2}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                      </svg>
                      Trending
                    </span>
                    <Link to="/search" className="nsearch__see-all" onClick={closeSearch}>See all</Link>
                  </div>
                  {recommended.length > 0 ? (
                    <div className="nsearch__grid">
                      {recommended.map((p, i) => <ProductCard key={p._id} p={p} index={i} />)}
                    </div>
                  ) : (
                    <div className="nsearch__grid nsearch__grid--skeleton">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="nsearch__skeleton">
                          <div className="nsearch__skeleton-img" />
                          <div className="nsearch__skeleton-line" />
                          <div className="nsearch__skeleton-line nsearch__skeleton-line--short" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── While typing: search results ── */}
              {isSearching && !sugLoading && suggestions.length > 0 && (
                <div className="nsearch__section">
                  <div className="nsearch__section-header">
                    <span className="nsearch__section-title">Results for "<strong>{searchQuery}</strong>"</span>
                    <button className="nsearch__see-all" onClick={submitSearch}>View all →</button>
                  </div>
                  <div className="nsearch__grid">
                    {suggestions.map((p, i) => <ProductCard key={p._id} p={p} index={i} />)}
                  </div>
                  {/* View all footer */}
                  <button className="nsearch__view-all-row" onClick={submitSearch}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    View all results for "<strong>{searchQuery}</strong>"
                  </button>
                </div>
              )}

              {/* ── No results ── */}
              {isSearching && !sugLoading && suggestions.length === 0 && (
                <div className="nsearch__empty">
                  <div className="nsearch__empty-icon">🌱</div>
                  <p className="nsearch__empty-title">No plants found for "<strong>{searchQuery}</strong>"</p>
                  <p className="nsearch__empty-sub">Try a different name, or browse our top sellers below</p>
                  <button className="nsearch__empty-btn" onClick={submitSearch}>Search anyway →</button>
                  {recommended.length > 0 && (
                    <>
                      <div className="nsearch__section-title" style={{ marginTop: 32 }}>You might like</div>
                      <div className="nsearch__grid" style={{ marginTop: 16 }}>
                        {recommended.slice(0, 6).map((p, i) => <ProductCard key={p._id} p={p} index={i} />)}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── Loading skeleton while searching ── */}
              {isSearching && sugLoading && (
                <div className="nsearch__section">
                  <div className="nsearch__grid nsearch__grid--skeleton">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="nsearch__skeleton">
                        <div className="nsearch__skeleton-img" />
                        <div className="nsearch__skeleton-line" />
                        <div className="nsearch__skeleton-line nsearch__skeleton-line--short" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </header>
  );
}